import { DictType } from "@pma/types"
import useTranslator from "@hooks/use-translator"
import { cn, formatTime, getUserFromId, parseMessage } from "@pma/utils/helpers"
import { formatDistance } from "date-fns"
import { he } from "date-fns/locale"
import { UserIcon } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar"
import { useAuth } from "@stores/auth-store"
import { useEffect, useMemo, useState } from "react"
import { Card } from "../common/card"

interface Props {
  activity: DictType
  reverse: boolean
  task: DictType
}
const TaskActivityCard = ({ activity, reverse, task }: Props) => {
  const { locale, translate } = useTranslator()
  const { authConfig } = useAuth()

  const isHebrew = locale === "he"

  const organizationUsers = authConfig?.organizationUsers
  const changes: DictType = activity?.changes?.changes ? activity?.changes?.changes[0] || {} : {}
  const prevValue = changes?.prev_value
  const newValue = changes?.new_value

  const [showMore, setShowMore] = useState(false)

  const responsibleUser = getUserFromId(activity?.changes?.by_user, organizationUsers)

  const parsedChanges: DictType = useMemo(() => {
    let temp = {}
    let valueKeys: any[] = []

    if (prevValue && Object.keys(prevValue).length > 0) {
      valueKeys = [...Object.keys(prevValue)]
    }
    if (newValue && Object.keys(newValue)?.length > 0) {
      valueKeys = [...Object.keys(newValue)]
    }

    if (changes?.column_name == "status_id") {
      temp = {
        name: "task status was changed",
        from: prevValue?.name,
        to: newValue?.name,
      }
    } else if (changes?.column_name == "" && valueKeys.includes("file")) {
      temp = {
        name: "file was added",
        from: "",
        to: "",
      }
    } else if (changes?.column_name == "notes_id") {
      temp = {
        name: "note was added",
        from: parseMessage(prevValue?.note, organizationUsers),
        to: parseMessage(newValue?.note, organizationUsers),
      }
    } else if (changes?.column_name == "" && valueKeys.includes("note")) {
      temp = {
        name: "note text was edited",
        from: parseMessage(prevValue?.note, organizationUsers),
        to: parseMessage(newValue?.note, organizationUsers),
      }
    } else if (changes?.column_name === "responsible_id") {
      temp = {
        name: `${translate("task assigned to")} ${newValue?.name}`,
        from: "",
        to: "",
      }
    } else if (changes?.column_name == "title") {
      temp = {
        name: "title was changed",
        from: prevValue?.title,
        to: newValue?.title,
      }
    } else if (changes?.column_name == "description") {
      temp = {
        name: `${translate("description was changed")}`,
        from: prevValue?.description || "",
        to: newValue?.description,
      }
    } else if (changes?.column_name == "" && changes?.change_action?.en == "subtask added") {
      temp = {
        name: `insert subtask added`,
        from: "",
        to: newValue?.title,
      }
    } else if (valueKeys.includes("title")) {
      temp = {
        name: "title",
        from: prevValue?.title,
        to: newValue?.title,
      }
    } else if (valueKeys.includes("construction_name")) {
      temp = {
        name: "construction name",
        from: prevValue?.construction_name,
        to: newValue?.construction_name,
      }
    } else if (valueKeys.includes("map_annotation")) {
      temp = {
        name: "map annotation",
        from: prevValue?.map_annotation?.annotation,
        to: newValue?.map_annotation?.annotation,
      }
    } else if (valueKeys.includes("annotation")) {
      temp = {
        name: "file",
        from: prevValue?.annotation && "Annotation",
        to: newValue?.annotation && "Annotation",
      }
    } else if (valueKeys.includes("address_text")) {
      temp = {
        name: "address",
        from: prevValue?.address_text,
        to: newValue?.address_text,
      }
    } else if (valueKeys.includes("finish_to") || valueKeys.includes("start_at")) {
      temp = {
        name: "task",
        from: "",
        to: (prevValue?.finish_to || prevValue?.start_at) && "Time tracking",
      }
    }

    return {
      ...temp,
      createdAt: activity?.created_at,
      updatedAt: activity?.changes_time,
    }
  }, [])

  useEffect(() => {
    if (parsedChanges?.to?.length > 20) setShowMore(true)
  }, [parsedChanges])

  const handleShowMore = () => setShowMore(false)
  const resetShowMore = () => setShowMore(true)

  return (
    <Card
      className={`flex h-fit w-full items-center overflow-hidden rounded-lg ${
        reverse ? "flex-row-reverse" : "flex-row"
      }`}
    >
      <div className={cn("flex w-full flex-col rounded-lg p-4")}>
        <div className='flex flex-1'>
          <div className='hidden md:flex'>
            <img
              src={
                responsibleUser?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`
              }
              alt={responsibleUser?.name}
              aria-hidden='true'
              className='mr-1 h-10 w-10 rounded-full'
            />
          </div>
          <div className='mx-2 flex w-full flex-col'>
            <div className='flex flex-col gap-2'>
              <div className='flex items-center gap-1 text-xs'>
                <span className='font-semibold'> {responsibleUser?.name} </span>

                <span className='flex items-center gap-2 font-semibold text-primary'>
                  <UserIcon size={16} className='text-primary' />
                  {activity?.singe_action?.action_id ? (
                    <div>
                      {isHebrew
                        ? activity.singe_action?._action?.he ||
                          translate(activity.singe_action?._action?.en)
                        : activity.singe_action?._action?.en}
                    </div>
                  ) : (
                    <div>
                      {isHebrew
                        ? activity.changes?._change_type?.he ||
                          translate(activity.changes?._change_type?.en)
                        : activity?.changes?._change_type?.en}
                    </div>
                  )}
                </span>
              </div>
              <div className='flex items-center gap-4'>
                <p
                  className='w-[100%] overflow-hidden text-ellipsis break-all py-1 text-xs'
                  onClick={() => {}}
                >
                  {translate(parsedChanges?.name)}&nbsp;{" "}
                  {parsedChanges?.from && (
                    <>
                      from{" "}
                      <p
                        dangerouslySetInnerHTML={{ __html: parsedChanges?.from }}
                        className='activity-list overflow-hidden text-ellipsis break-all py-1 text-sm md:max-w-[44ch]'
                      />
                    </>
                  )}
                  {parsedChanges?.to && (
                    <>
                      to{" "}
                      <p
                        dangerouslySetInnerHTML={{ __html: parsedChanges?.to }}
                        className={`activity-list overflow-hidden text-ellipsis break-all py-1 text-sm ${showMore ? "h-[25px] overflow-hidden" : parsedChanges?.to?.length > 20 ? "h-[60px] overflow-y-scroll" : "h-[60px]"}`}
                      />
                      {showMore && (
                        <button className='font-bold' onClick={handleShowMore}>
                          {translate("...show more")}
                        </button>
                      )}
                      {!showMore && parsedChanges?.to?.length > 20 && (
                        <button className='font-bold' onClick={resetShowMore}>
                          {translate("hide")}
                        </button>
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className='flex w-full cursor-pointer items-center justify-end'>
              {activity?.recepients?.map((user: DictType, index: number) => (
                <Tooltip key={index} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <div className={""}>
                      <Avatar className={`ml-[-10px] h-7 w-7`}>
                        <AvatarImage
                          src={
                            user.user_picture
                              ? user.user_picture.url
                              : `${process.env.PUBLIC_URL}/usr.svg`
                          }
                          alt='Avatar'
                        />
                        <AvatarFallback className='uppercase'>
                          {user?.first_name && user?.first_name[0]}
                          {user?.last_name && user?.last_name[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span className='sr-only'>{user.name}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side='bottom' className='flex items-center gap-4'>
                    {user.name}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
        </div>

        <div className={`flex items-end justify-end text-xs text-slate-500`}>
          <p>
            <small>
              {formatDistance(parsedChanges?.createdAt, new Date(), {
                locale: isHebrew ? he : undefined,
                addSuffix: true,
              })}
            </small>{" "}
            <small> @ {formatTime(parsedChanges?.createdAt, { amPm: true })} </small>{" "}
          </p>
        </div>
      </div>
    </Card>
  )
}

export default TaskActivityCard
